// MODE.Cfix.B1 — Autonomous Transitions panel.
//
// Mode transparency for the project dashboard (Option C now-fix, second
// half). Makes a user AWARE of their delivery-mode reality:
//   • Accelerate: which phase EXIT gates advance AUTOMATICALLY (no
//     checkpoint row) vs PAUSE for human review (a project_review_-
//     checkpoints row exists at that phase's EXIT gate).
//   • Governed: every phase exit pauses — rendered as a compact state, not
//     a misleading per-phase "auto-advance" list.
//
// All data already exists server-side: `deliveryMode` rides on the project
// payload (passed in as a prop) and the per-phase checkpoints come from the
// EXISTING GET /projects/:id/checkpoints — this panel is its first web
// caller. The auto-vs-pause split is computed from the fetched rows crossed
// with VISIBLE_PHASES, NOT hardcoded to the 1/5/7 default, so a project with
// a customised checkpoint profile renders correctly from its own data.
//
// Scope (B1): read-only awareness only. No checkpoint editing (checkpoints
// are write-once; the post-creation editor is a deferred follow-on). No
// real-time "about to advance" warning (needs backend pre-transition
// eventing that does not exist — recon phase-2, out of scope).

import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';
import { VISIBLE_PHASES } from './ProjectPhaseStepper';
import { phaseConfig, PHASE_DISPLAY_NUMBER } from './PhaseBadge';

interface Props {
  projectId: string;
  /** Current delivery mode from the project payload (projects.delivery_mode). */
  deliveryMode: 'governed' | 'accelerate';
}

/** Minimal shape of a row from GET /projects/:id/checkpoints. */
interface CheckpointRow {
  phase:    string;
  gateType: string;
}

const cardCls = 'rounded-xl border shadow-sm';
const cardStyle: React.CSSProperties = {
  background: 'var(--surface)', borderColor: 'var(--border)', padding: 'var(--space-5)',
};
const sectionLabel: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
  color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em',
  margin: 0,
};
// "Auto-advances" uses --signal (Accelerate green); "Pauses for your review"
// uses --primary (Governed violet) — same colour language as DeliveryModeBadge.
const autoBadge: React.CSSProperties = {
  flexShrink: 0, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
  color: 'var(--signal)', background: 'rgba(0,229,160,0.12)', border: '1px solid rgba(0,229,160,0.30)',
  whiteSpace: 'nowrap',
};
const pauseBadge: React.CSSProperties = {
  flexShrink: 0, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
  color: 'var(--primary)', background: 'rgba(124,58,237,0.10)', border: '1px solid rgba(124,58,237,0.30)',
  whiteSpace: 'nowrap',
};

/** "1", "1 and 5", "1, 5, and 7" — Oxford-comma join for the indicator copy. */
function formatPhaseList(items: string[]): string {
  if (items.length <= 1) return items.join('');
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

const Header = () => (
  <p
    className="uppercase tracking-wider"
    style={{ ...sectionLabel, borderLeft: '3px solid var(--primary)', paddingLeft: 8, marginBottom: 12 }}
  >
    Autonomous Transitions
  </p>
);

export default function AutonomousTransitionsPanel({ projectId, deliveryMode }: Props) {
  const isAccelerate = deliveryMode === 'accelerate';
  const [checkpointPhases, setCheckpointPhases] = useState<Set<string> | null>(null);
  // Only Accelerate fetches; Governed renders the compact state with no fetch.
  const [loading, setLoading] = useState(isAccelerate);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Local closure flag for per-run cancellation (matches the dashboard's
    // own fetch effects). The parent keys this component by projectId +
    // deliveryMode (see ProjectDashboardPage), so a mode toggle or a project
    // switch REMOUNTS it — the mount-time useState defaults are the reset, so
    // no stale loading/error/checkpointPhases can leak across identities.
    let cancelled = false;
    // Governed projects have no checkpoint rows and never auto-advance, so
    // there is nothing to fetch — the all-pause state is rendered below.
    if (!isAccelerate || !projectId) return;
    apiFetch<{ checkpoints: CheckpointRow[] }>(`/projects/${projectId}/checkpoints`)
      .then(({ checkpoints }) => {
        if (cancelled) return;
        // A phase PAUSES when it has an EXIT checkpoint row; otherwise its
        // exit gate auto-advances. Computed from data — never hardcoded.
        const exitCheckpointPhases = new Set(
          checkpoints.filter(c => c.gateType === 'EXIT').map(c => c.phase),
        );
        setCheckpointPhases(exitCheckpointPhases);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectId, isAccelerate]);

  // ── Governed: compact all-pause state (no misleading auto-advance list) ──
  if (!isAccelerate) {
    return (
      <div data-testid="autonomous-transitions-panel" data-mode="governed" className={cardCls} style={cardStyle}>
        <Header />
        <p style={{ fontSize: 13, color: 'var(--text)', margin: 0, lineHeight: 1.5 }}>
          Every phase exit waits for your approval. No phase advances to the next without you — you confirm each
          transition, even when the gate criteria pass.
        </p>
      </div>
    );
  }

  // ── Accelerate ──────────────────────────────────────────────────────────
  const pausePhaseNumbers = checkpointPhases
    ? VISIBLE_PHASES.filter(p => checkpointPhases.has(p)).map(p => PHASE_DISPLAY_NUMBER[p])
    : [];

  return (
    <div data-testid="autonomous-transitions-panel" data-mode="accelerate" className={cardCls} style={cardStyle}>
      <Header />

      {/* (c) Persistent indicator — always shown, NO data dependency, so it
          never flashes/jumps while the checkpoint fetch is in flight. The
          project's actual pause phases are named in the data-driven line
          below once loaded. */}
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
        Phases advance automatically when their gates pass — you&apos;re notified after each.
      </p>

      {loading && (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>Loading…</p>
      )}
      {error && (
        <p
          data-testid="autonomous-transitions-error"
          style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}
        >
          Couldn&apos;t load transition details. Refresh to try again.
        </p>
      )}

      {!loading && !error && checkpointPhases && (
        <>
          {pausePhaseNumbers.length > 0 && (
            <p style={{ fontSize: 12, color: 'var(--text)', margin: '0 0 8px', lineHeight: 1.5 }}>
              Review pauses at {pausePhaseNumbers.length === 1 ? 'Phase' : 'Phases'}{' '}
              {formatPhaseList(pausePhaseNumbers)}.
            </p>
          )}
          <div role="list" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {VISIBLE_PHASES.map((phase) => {
              const pauses = checkpointPhases.has(phase);
              return (
                <div
                  key={phase}
                  role="listitem"
                  data-testid={`autonomous-phase-${phase}`}
                  data-behavior={pauses ? 'pause' : 'auto'}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 12,
                  }}
                >
                  <span style={{ color: 'var(--text)' }}>
                    Phase {PHASE_DISPLAY_NUMBER[phase]} — {phaseConfig[phase].label}
                  </span>
                  <span style={pauses ? pauseBadge : autoBadge}>
                    {pauses ? 'Pauses for your review' : 'Auto-advances'}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
